import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Avatar, Button, Grid2 as Grid, Stack, Tooltip } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StyledDrawer from "@components/StyledDrawer";
import { Patient } from "@models/patient";
import { DateUtil } from "@utils/date.util";
import {
  getPatientDisplayName,
  getPatientStatusKey,
} from "@utils/patient.util";

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 16px;
`;

const Title = styled.div`
  font-size: 32px;
  line-height: 48px;
  font-weight: 400;
  color: #171a1fff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  flex: 1;
  min-height: 0;
`;

const Label = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  color: #6f7787ff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Value = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  color: #171a1fff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const PatientDrawer: FC<{
  patient: Patient | undefined;
  onClose: () => void;
  onToggleActive: (patient: Patient) => void;
  onArchiveToggle: (patient: Patient) => void;
  onOpenPortal: (patient: Patient) => void;
  onEditProfile: (patient: Patient) => void;
  isMutating?: boolean;
}> = ({
  patient,
  onClose,
  onToggleActive,
  onArchiveToggle,
  onOpenPortal,
  onEditProfile,
  isMutating = false,
}) => {
  const { t } = useTranslation();

  return (
    <StyledDrawer onClose={onClose} open={!!patient} width="600px">
      <DrawerContent>
        <Header>
          {patient && (
            <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
              <Avatar
                sx={{ width: 56, height: 56, flexShrink: 0 }}
                alt={getPatientDisplayName(patient)}
                src={patient.avatarUrl}
              >
                <PersonOutlineIcon />
              </Avatar>
              <Title>{getPatientDisplayName(patient)}</Title>
            </Stack>
          )}
        </Header>

        {patient && (
          <Body>
            <Grid container spacing={1}>
              <Grid size={4}>
                <Label>{t("patientManage.columns.status")}</Label>
              </Grid>
              <Grid size={8}>
                <Value>
                  {t(
                    `patientManage.status.${getPatientStatusKey(patient)}`,
                  )}
                </Value>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid size={4}>
                <Label>{t("patientManage.columns.lastActivity")}</Label>
              </Grid>
              <Grid size={8}>
                <Value>
                  {patient.lastActivityAt
                    ? DateUtil.formatDateTime(patient.lastActivityAt)
                    : t("patientManage.never")}
                </Value>
              </Grid>
            </Grid>

            <Actions>
              <Button
                variant="outlined"
                fullWidth
                disabled={isMutating}
                onClick={() => onToggleActive(patient)}
              >
                {patient.isActive
                  ? t("patientManage.deactivate")
                  : t("patientManage.activate")}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                disabled={isMutating}
                onClick={() => onArchiveToggle(patient)}
              >
                {patient.archivedAt
                  ? t("patientManage.unarchive")
                  : t("patientManage.archive")}
              </Button>
              <Tooltip
                title={
                  !patient.isActive ? t("patientManage.portalInactive") : ""
                }
              >
                <span>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<HomeOutlinedIcon />}
                    disabled={!patient.isActive || isMutating}
                    onClick={() => onOpenPortal(patient)}
                  >
                    {t("patientManage.openPortal")}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="text"
                fullWidth
                onClick={() => onEditProfile(patient)}
              >
                {t("patientManage.editProfile")}
              </Button>
            </Actions>
          </Body>
        )}
      </DrawerContent>
    </StyledDrawer>
  );
};

export default PatientDrawer;
